import type { Dictionary } from './dictionary.js';
import { resolveBlanks } from './blanks.js';
import { extractRuns, type Grid } from './grid.js';
import { faceOf } from './tiles.js';
import { keyOf, type Coord, type CoordKey, type Letter, type MatchConfig, type Orientation, type Seat } from './types.js';

/**
 * One word's contribution.
 *
 * A letter earns its face value **once for every word it belongs to**, so a
 * letter at a crossing is paid twice — once across, once down. That is the
 * whole point of building a dense grid rather than a long one, and it is why
 * the breakdown lists words rather than tiles.
 */
export interface ScoredWord {
  readonly word: string;
  readonly orientation: Orientation;
  readonly start: Coord;
  readonly cells: readonly Coord[];
  readonly points: number;
  readonly valid: boolean;
}

export interface ScoreOptions {
  /** This seat used every letter and ended the match, so takes the bonus. */
  wentOut?: boolean;
  /**
   * Whether the dictionary may be consulted.
   *
   * False makes every readable, long-enough run count provisionally, without
   * asking whether it is a word. That is what the in-match display needs: a
   * running total that moves with what you have built, and reveals nothing a
   * player was not entitled to. A score that quietly dropped the moment you
   * laid an invalid word would be a free, unlimited word check, which is
   * exactly what the check policy exists to ration.
   */
  decide?: boolean;
}

/** A letter that earned nothing, and why. */
export interface UnusedLetter {
  readonly letter: Letter | null;
  readonly value: number;
  /**
   * In hand, or sitting on the grid in no word the dictionary accepts —
   * either alone, or inside a run that was rejected.
   */
  readonly where: 'rack' | 'stranded';
}

export interface ScoreBreakdown {
  readonly words: readonly ScoredWord[];
  /** Everything earned from words, crossings counted twice. */
  readonly wordPoints: number;
  readonly unused: readonly UnusedLetter[];
  /** Face value of every letter that earned nothing. Subtracted. */
  readonly penalty: number;
  /** Awarded to whoever used every letter and ended the match. */
  readonly bonus: number;
  readonly total: number;
  /** True when every run on the grid is a word, and there is at least one. */
  readonly allWordsValid: boolean;
  /** False when the dictionary was not consulted, so validity is provisional. */
  readonly decided: boolean;
}

/**
 * Score one seat.
 *
 * One rule, stated once: **a letter is paid for every word it is in, and
 * charged for if it is in none.** A letter stranded on the grid outside any
 * word costs the same as one left in hand, because in both cases you did not
 * manage to use it.
 *
 * "Any word" means any word the dictionary accepts. A rejected run earns
 * nothing and shelters nothing: without that, parking your worst letters in
 * invented words would be strictly better than leaving them in hand, and the
 * penalty would stop meaning anything.
 */
export function scoreSeat(
  seat: Pick<Seat, 'grid' | 'rack'>,
  dict: Dictionary,
  config: Pick<MatchConfig, 'minWordLength' | 'goingOutBonus'>,
  opts: ScoreOptions = {},
): ScoreBreakdown {
  const decide = opts.decide ?? true;
  /*
   * Blanks stand for whatever makes the grid work.
   *
   * Scoring cannot be left holding a '?': the run would be unreadable and its
   * letters charged as unused, which would punish a player for a blank the
   * rules say they never had to pin down. A blank is worth nothing either way,
   * so which letter it turns out to be changes no total — only whether the
   * words around it count.
   */
  const resolved = decide ? resolveBlanks(seat.grid, dict, config.minWordLength) : null;
  const grid: Grid = resolved?.grid ?? seat.grid;

  /** Cells that are in at least one accepted word, and so earned something. */
  const earning = new Set<CoordKey>();

  const words: ScoredWord[] = extractRuns(grid).map((run) => {
    // An unassigned blank leaves the run unreadable, so it cannot be judged.
    const valid = !run.word.includes('?')
      && run.cells.length >= config.minWordLength
      && (!decide || dict.has(run.word));
    let points = 0;
    for (const cell of run.cells) {
      points += grid.get(keyOf(cell))?.value ?? 0;
      if (valid) earning.add(keyOf(cell));
    }
    return {
      word: run.word, orientation: run.orientation, start: run.start,
      cells: run.cells, points: valid ? points : 0, valid,
    };
  });

  const wordPoints = words.reduce((sum, w) => sum + w.points, 0);

  const unused: UnusedLetter[] = [];
  for (const [k, tile] of grid) {
    if (earning.has(k)) continue;
    unused.push({ letter: faceOf(tile), value: tile.value, where: 'stranded' });
  }
  for (const tile of seat.rack) {
    unused.push({ letter: faceOf(tile), value: tile.value, where: 'rack' });
  }

  const penalty = unused.reduce((sum, u) => sum + u.value, 0);
  const bonus = opts.wentOut ? config.goingOutBonus : 0;

  return {
    words, wordPoints, unused, penalty, bonus,
    total: wordPoints - penalty + bonus,
    allWordsValid: words.length > 0 && words.every((w) => w.valid),
    decided: decide,
  };
}

/**
 * Just the number, for a running total in the corner of the screen.
 *
 * `decide` defaults to false here, unlike {@link scoreSeat}: this is the
 * in-match display, and it must not become a word checker.
 */
export function scoreTotal(
  seat: Pick<Seat, 'grid' | 'rack'>,
  dict: Dictionary,
  config: Pick<MatchConfig, 'minWordLength' | 'goingOutBonus'>,
  decide = false,
): number {
  return scoreSeat(seat, dict, config, { decide }).total;
}
